import { RawSexEnumT, type PHRPatientInfoT } from '@lotushealth/lotushealth_js_sdk/schemas';

export type PatientProfileField = Exclude<keyof PHRPatientInfoT, 'name' | 'origin_ids'>;

export interface PatientProfileEntry {
  field: PatientProfileField;
  label: string;
  value: string;
}

export interface PatientProfile {
  heading: string;
  entries: PatientProfileEntry[];
}

const EMPTY_VALUE = '—';

const FIELD_ORDER: readonly PatientProfileField[] = [
  'date_of_birth',
  'age',
  'sex',
  'phone',
  'email',
  'address',
  'primary_pharmacy',
  'insurance',
  'primary_language',
  'emergency_contact',
  'med_history_consent_at',
  'id',
];

const FIELD_LABELS: Readonly<Record<PatientProfileField, string>> = {
  id: 'id',
  age: 'age',
  date_of_birth: 'date of birth',
  sex: 'sex',
  primary_language: 'primary language',
  phone: 'phone',
  email: 'email',
  address: 'address',
  insurance: 'insurance',
  primary_pharmacy: 'primary pharmacy',
  emergency_contact: 'emergency contact',
  med_history_consent_at: 'med history consent',
};

type SexAbbreviation = 'M' | 'F' | 'O' | '';

function toSexAbbreviation(sex: PHRPatientInfoT['sex']): SexAbbreviation {
  switch (sex) {
    case RawSexEnumT.male:
      return 'M';
    case RawSexEnumT.female:
      return 'F';
    case RawSexEnumT.other:
      return 'O';
    default:
      return '';
  }
}

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function tryFormatDatetime(value: string): string | null {
  if (!ISO_DATETIME_RE.test(value)) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

type PatientProfileFieldValue = PHRPatientInfoT[PatientProfileField];

function formatAge(age: PHRPatientInfoT['age']): string {
  return Number.isFinite(age) ? String(age) : '';
}

function formatValue(value: PatientProfileFieldValue): string {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY_VALUE;
  }
  if (value.length === 0) {
    return EMPTY_VALUE;
  }
  return tryFormatDatetime(value) ?? value;
}

function buildHeading(info: PHRPatientInfoT): string {
  const suffix = `${formatAge(info.age)}${toSexAbbreviation(info.sex)}`;
  return [info.name.trim(), suffix].filter((part) => part.length > 0).join(', ');
}

export function toPatientProfile(info: PHRPatientInfoT): PatientProfile {
  const entries: PatientProfileEntry[] = FIELD_ORDER.map((field) => ({
    field,
    label: FIELD_LABELS[field],
    value: formatValue(info[field]),
  }));

  return {
    heading: buildHeading(info),
    entries,
  };
}
